import { Credit, Subscription } from '~/jobiqo/types/types';
import { UseCreditsInterface } from '../../Credits/hooks/useCredits';
import { UseSubscriptionsInterface } from '../../Subscriptions/hooks/useSubscriptions';

/**
 * Show the modal calculation
 *
 * @param needsApproval
 *  If the contact request needs approval.
 * @param hasFlatrateSubscription
 *  If the user has a flat rate subscription.
 * @param userResumeContactSlotsActive
 *  If the subscription is limited to slot amount.
 * @param userResumeContactSlotsAvailable
 *  The amount of slots available.
 * @param userResumeCredits
 *  The amount of credits the user has.
 *
 * @returns
 *  If the modal should be shown.
 */
export function showModalCalculation(
  needsApproval: boolean,
  hasFlatrateSubscription: boolean,
  userResumeContactSlotsActive: boolean,
  userResumeContactSlotsAvailable: number,
  userResumeCredits: number,
) {
  // The user has a flat rate subscription,
  // the subscription is limited to slots amount
  // and the user has no credits left.
  // (needs to buy credits)
  const showNoSlotsModal =
    hasFlatrateSubscription &&
    userResumeContactSlotsActive &&
    userResumeContactSlotsAvailable <= 0;

  // The user has no resume contact credits left and no flat rate subscription
  // (needs to buy credits)
  const showNoCreditsModal = userResumeCredits <= 0 && !hasFlatrateSubscription;

  // The user has a flat rate subscription, subscription is limited to slots but still has slots
  // or the user has a flat rate subscription and subscription is not limited to slots
  // or the user has credits left and no flat rate subscription
  // (can send contact request and an invite message is needed -> needs approval)
  const showContactRequestForm =
    needsApproval &&
    ((hasFlatrateSubscription &&
      userResumeContactSlotsActive &&
      userResumeContactSlotsAvailable > 0) ||
      (hasFlatrateSubscription && !userResumeContactSlotsActive) ||
      (userResumeCredits > 0 && !hasFlatrateSubscription));
  return (
    needsApproval || // If needs approval we always show the modal
    showNoSlotsModal ||
    showNoCreditsModal ||
    showContactRequestForm
  );
}

/**
 *  Calculate the user subscription values
 *
 * @param userSubscriptions
 *  The user subscriptions
 * @param credits
 *  The user credits
 * @returns
 *  The user subscription values
 */
export const calculateUserSubscriptionValues = (
  userSubscriptions: UseSubscriptionsInterface,
  credits: UseCreditsInterface,
) => {
  const hasFlatrateSubscription = userSubscriptions.data.some(
    (subscription: Subscription) =>
      subscription.type === 'resume_contact_request_flatrate',
  );

  const userResumeContactSlotsAvailable = userSubscriptions.data.filter(
    (subscription: Subscription) =>
      subscription.type === 'resume_contact_request_flatrate' &&
      subscription.status?.value === 'ACTIVE',
  )?.[0]?.usageResumeContactSlots?.available as number;

  const userResumeContactSlotsActive = userSubscriptions.data.filter(
    (subscription: Subscription) =>
      subscription.type === 'resume_contact_request_flatrate' &&
      subscription.status?.value === 'ACTIVE',
  )?.[0]?.usageResumeContactSlots?.active as boolean;

  const userResumeCredits = credits.data.filter(
    (credit: Credit) => credit.type === 'RESUME_CONTACT_CREDIT',
  )?.[0]?.amount as number;

  return {
    hasFlatrateSubscription,
    userResumeContactSlotsAvailable,
    userResumeContactSlotsActive,
    userResumeCredits,
  };
};
